import { motion, useReducedMotion } from "framer-motion";
import { useTheme } from "../ThemeContext";
import { Badge } from "../ui/badge";
import ShinyText from "../reactbits/ShinyText";
import { BellRing, RefreshCw, ShieldCheck, Smartphone, Sparkles, Video } from "lucide-react";
import PinnedCardStack from "./PinnedCardStack";

// Accent colours come from the Quilt logo (teal, blue, purple, magenta)
const cards = [
    {
        title: "Hyper-Personalized Videos",
        description: "Short-form videos are automatically generated and tailored to each individual based on their health conditions, personal goals, preferences, and day-to-day context.",
        icon: Video,
        accent: "from-[#7342b2] to-[#974ba1]",
        glow: "bg-[#974ba1]/30",
    },
    {
        title: "Adaptive Learning Engine",
        description: "Our platform continuously learns from patient interactions, behavioral signals, and patient reports, optimizing each new content generated to maximize impact on well-being and drive engagement.",
        icon: RefreshCw,
        accent: "from-[#42729e] to-[#2382a1]",
        glow: "bg-[#42729e]/30",
    },
    {
        title: "Enterprise Safety & Guardrails",
        description: "Built with rigorous medical guardrails. Your team has full oversight to review and approve every video before it’s published.",
        icon: ShieldCheck,
        accent: "from-[#da3594] to-[#e943a0]",
        glow: "bg-[#da3594]/30",
    },
    {
        title: "Daily Mobile Feed",
        description: "Patients receive a curated daily wellness feed directly on their smartphones, making health guidance feel as natural and familiar as social media.",
        icon: Smartphone,
        accent: "from-[#17a1a8] to-[#2382a1]",
        glow: "bg-[#17a1a8]/30",
    },
    {
        title: "Dynamic Nudges & Assessments",
        description: "Embedded micro-assessments, health checks, and dynamic calls-to-action seamlessly nudge patients to complete care tasks, report symptoms, or book their next appointment.",
        icon: BellRing,
        accent: "from-[#974ba1] to-[#da3594]",
        glow: "bg-[#974ba1]/30",
    },
];

// Mobile deck: the first four cards are swiped away in turn; the last stays so the section ends on a full card
const EXIT_RANGES: [number, number][] = [
    [0.05, 0.25],
    [0.28, 0.48],
    [0.51, 0.71],
    [0.74, 0.94],
];

type Card = (typeof cards)[number];

/** Compact card for the mobile deck: small icon, title and a line or two of text. */
function DeckFace({ card, index, dark }: { card: Card; index: number; dark: boolean }) {
    const Icon = card.icon;
    return (
        <div
            className={`relative flex h-full w-full flex-col justify-between overflow-hidden rounded-3xl border p-5 text-left shadow-2xl ${dark
                ? "bg-slate-900 border-slate-700/60 shadow-black/50"
                : "bg-white border-slate-200 shadow-slate-300/60"
                }`}
        >
            <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-3xl [clip-path:inset(0_round_1.5rem)]">
                <div className={`absolute -right-10 -top-10 h-40 w-40 rounded-full blur-3xl ${card.glow}`} />
            </div>
            <div className="relative flex items-start justify-between">
                <div className={`flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br text-white shadow-lg ${card.accent}`}>
                    <Icon className="h-5 w-5" />
                </div>
                <span className={`text-sm font-semibold tabular-nums ${dark ? "text-slate-500" : "text-slate-400"}`}>
                    0{index + 1} / 0{cards.length}
                </span>
            </div>
            <div className="relative">
                <h3 className={`mb-1 text-xl font-bold ${dark ? "text-white" : "text-slate-900"}`}>{card.title}</h3>
                <p className={`text-sm leading-relaxed ${dark ? "text-slate-400" : "text-slate-600"}`}>{card.description}</p>
            </div>
        </div>
    );
}

/** Desktop tile: all cards side by side, rising in one after another as the section scrolls into view. */
function Tile({ card, index, dark, animate }: { card: Card; index: number; dark: boolean; animate: boolean }) {
    const Icon = card.icon;
    return (
        <motion.li
            initial={animate ? { opacity: 0, y: 48 } : false}
            whileInView={animate ? { opacity: 1, y: 0 } : undefined}
            viewport={{ once: true, margin: "0px 0px -80px 0px" }}
            transition={{ duration: 0.6, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
            className={`group relative w-full overflow-hidden rounded-3xl border p-6 text-left transition-transform duration-300 hover:-translate-y-1 sm:w-[calc(50%-0.5rem)] md:w-[calc(33.333%-0.67rem)] xl:w-[calc(20%-0.8rem)] ${dark
                ? "bg-slate-900 border-slate-700/60"
                : "bg-white border-slate-200 shadow-lg shadow-slate-200/70"
                }`}
        >
            <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-3xl [clip-path:inset(0_round_1.5rem)]">
                <div className={`absolute -right-10 -top-10 h-40 w-40 rounded-full opacity-70 blur-3xl transition-opacity duration-300 group-hover:opacity-100 ${card.glow}`} />
            </div>
            <div className="relative">
                <div className={`mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br text-white shadow-lg ${card.accent}`}>
                    <Icon className="h-7 w-7" />
                </div>
                <h3 className={`mb-2 text-xl font-bold ${dark ? "text-white" : "text-slate-900"}`}>{card.title}</h3>
                <p className={`text-sm leading-relaxed ${dark ? "text-slate-400" : "text-slate-600"}`}>{card.description}</p>
            </div>
        </motion.li>
    );
}

export function WhatIsQuilt() {
    const { theme } = useTheme();
    const dark = theme === "dark";
    const reduceMotion = useReducedMotion();

    const intro = (
        <div className="text-center">
            <Badge variant="outline" className="mb-3 px-4 py-2 bg-[#974ba1]/10 border-[#974ba1]/30">
                <Sparkles className="w-4 h-4 mr-2 text-[#974ba1]" />
                <ShinyText text="What is Quilt?" speed={3} className="font-medium" />
            </Badge>
            <h2 className={`mx-auto mb-4 max-w-4xl text-balance text-3xl md:text-5xl font-bold ${dark ? "text-white" : "text-slate-900"}`}>
                Continuous Care, Delivered Through Personalized Video
            </h2>
            <p className={`mx-auto max-w-3xl text-base md:text-lg leading-relaxed ${dark ? "text-slate-400" : "text-slate-600"}`}>
                Quilt is a generative content platform that keeps patients and their care teams connected between visits. Our
                platform predicts, generates, and serves short-form wellness videos tailored to each patient’s condition,
                preferences, and individual context, optimizing for engagement and well-being.
            </p>
        </div>
    );

    const tiles = (animate: boolean) => (
        <ul className="mx-auto flex max-w-7xl flex-wrap justify-center gap-4">
            {cards.map((card, index) => (
                <Tile key={card.title} card={card} index={index} dark={dark} animate={animate} />
            ))}
        </ul>
    );

    // Reduced motion: no scroll-driven deck on mobile and no rise-in on desktop; the tiles are simply stacked
    if (reduceMotion) {
        return (
            <div className="mx-auto w-full px-4 py-10 md:py-24">
                <div className="mb-8 md:mb-14">{intro}</div>
                {tiles(false)}
            </div>
        );
    }

    return (
        <>
            {/* Mobile: the intro reads as normal text, then the cards pin and are swiped away one by one */}
            <div className="md:hidden">
                <div className="px-4 pb-4 pt-10">{intro}</div>
                <PinnedCardStack
                    count={cards.length}
                    header={null}
                    renderCard={(i) => <DeckFace card={cards[i]} index={i} dark={dark} />}
                    exitRanges={EXIT_RANGES}
                    trackVh={280}
                    fit
                    cardClass="h-[min(290px,46svh)] w-full max-w-sm"
                />
            </div>
            {/* Desktop: side by side */}
            <div className="hidden px-4 py-24 md:block">
                <div className="mb-14">{intro}</div>
                {tiles(true)}
            </div>
        </>
    );
}

export default WhatIsQuilt;
